import { z } from 'zod';

import { isMoney, type Money } from '@nucleus-commerce/core';

const moneyValue = z.custom<Money>(isMoney).nullable();

/** A DatePicker value (YYYY-MM-DD); blank means "now" and is left to the backend. */
const DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The amount is only read when `partial` is on; a full settlement sends no amount and lets the
 * backend take the outstanding balance it holds. The balance check needs the order, hence the
 * factory. `today` is the store's calendar day: a date is "in the future" when it is after that
 * day, not after the UTC day, so a shop ahead of UTC can record this morning's wire.
 */
export function markPaidSchema(outstanding: Money, today: string) {
  return z
    .object({
      partial: z.boolean(),
      amount: moneyValue,
      reference: z.string().trim().max(200),
      paidAt: z.string().refine((v) => v === '' || DATE.test(v)),
      note: z.string().trim().max(1000),
    })
    .superRefine((v, ctx) => {
      if (!v.partial) return;
      if (v.amount === null) {
        ctx.addIssue({
          code: 'custom',
          path: ['amount'],
          message: 'orders:markPaid.validation.amountRequired',
        });
      } else if (v.amount.amount <= 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['amount'],
          message: 'orders:markPaid.validation.amountPositive',
        });
      } else if (v.amount.amount > outstanding.amount) {
        ctx.addIssue({
          code: 'custom',
          path: ['amount'],
          message: 'orders:markPaid.validation.overBalance',
        });
      }
    })
    .refine((v) => v.paidAt === '' || v.paidAt <= today, {
      path: ['paidAt'],
      error: 'orders:markPaid.validation.paidAtFuture',
    });
}

export type MarkPaidFormInput = z.input<ReturnType<typeof markPaidSchema>>;
export type MarkPaidFormOutput = z.output<ReturnType<typeof markPaidSchema>>;
